import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Button, 
  Input, 
  Space, 
  Card, 
  Typography, 
  Spin, 
  Modal, 
  Radio,
  message,
  Tabs,
  Table,
  Tag,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
  DatePicker
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  UserOutlined,
  CarOutlined,
  TruckOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  WhatsAppOutlined,
  SendOutlined
} from '@ant-design/icons';
import { deliveryPersonsApi, ordersApi, whatsappApi } from '../utils/api';
import DeliveryPersonList from '../components/delivery-persons/DeliveryPersonList';
import moment from 'moment';


const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const DeliveryPersons = () => {
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);
  
  // États pour l'onglet commandes
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState(null);
  const [deliveryPersonOrders, setDeliveryPersonOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderDateFilter, setOrderDateFilter] = useState(moment());
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [deliveryPersonStats, setDeliveryPersonStats] = useState({});
  
  // États pour l'envoi WhatsApp
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);

  useEffect(() => {
    fetchDeliveryPersons();
  }, [showActiveOnly]);

  const fetchDeliveryPersons = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (showActiveOnly) {
        filters.is_active = true;
      }
      const response = await deliveryPersonsApi.getAll(filters);
      setDeliveryPersons(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des livreurs:', error);
      message.error('Erreur lors de la récupération des livreurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (person) => {
    setPersonToDelete(person);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deliveryPersonsApi.delete(personToDelete.id);
      message.success('Livreur supprimé avec succès');
      fetchDeliveryPersons();
    } catch (error) {
      console.error('Erreur lors de la suppression du livreur:', error);
      message.error('Erreur lors de la suppression du livreur');
    } finally {
      setDeleteModalVisible(false);
      setPersonToDelete(null);
    }
  };

  // Fonction pour charger les commandes selon les filtres (livreur, date de livraison, statut)
  const fetchDeliveryPersonOrders = async (personId, date = orderDateFilter, status = orderStatusFilter) => {
    console.log('fetchDeliveryPersonOrders', personId, date, status);
    // Vérifier s'il y a au moins un critère de recherche
    if (!personId && !date && (!status || status === 'all')) {
      setDeliveryPersonOrders([]);
      return;
    }
    
    try {
      setLoadingOrders(true);
      
      const filters = {
        include_items: false,
        order_by: 'delivery_date', // Trier par date de livraison
        ascending: false
      };
      
      // Filtre par livreur si spécifié
      if (personId) {
        filters.delivery_person_id = personId;
      }
      
      // Filtre par date de livraison si spécifiée
      if (date) {
        const dateStr = date.format('YYYY-MM-DD');
        filters.delivery_date_from = `${dateStr} 00:00:00`;
        filters.delivery_date_to = `${dateStr} 23:59:59`;
      }
      
      // Filtre par statut si spécifié
      if (status && status !== 'all') {
        filters.status = status;
      }
      
      const response = await ordersApi.getAll(filters);
      setDeliveryPersonOrders(response?.data || []);
      
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      message.error('Erreur lors du chargement des commandes');
      setDeliveryPersonOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fonction pour charger les statistiques d'un livreur
  const fetchDeliveryPersonStats = async (personId) => {
    if (!personId) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const statsResponse = await deliveryPersonsApi.getDailyStats(personId, today);
      
      if (statsResponse && statsResponse.data) {
        setDeliveryPersonStats(statsResponse.data);
      } else {
        setDeliveryPersonStats({ orders_count: 0 });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setDeliveryPersonStats({ orders_count: 0 });
    }
  };

  // Gestionnaire de changement de livreur sélectionné
  const handleDeliveryPersonChange = (personId) => {
    setSelectedDeliveryPerson(personId);
    // Toujours faire la requête, même si aucun livreur n'est sélectionné
    fetchDeliveryPersonOrders(personId, orderDateFilter, orderStatusFilter);
    
    // Les stats ne sont pertinentes que pour un livreur spécifique
    if (personId) {
      fetchDeliveryPersonStats(personId);
    } else {
      setDeliveryPersonStats({});
    }
  };

  // Gestionnaire de changement de date
  const handleDateFilterChange = (date) => {
    setOrderDateFilter(date);
    console.log('handleDateFilterChange', date, selectedDeliveryPerson, orderStatusFilter);
      fetchDeliveryPersonOrders(selectedDeliveryPerson, date, orderStatusFilter);
  };

  // Gestionnaire de changement de statut
  const handleStatusFilterChange = (status) => {
    setOrderStatusFilter(status);
    console.log('handleStatusFilterChange', status, selectedDeliveryPerson, orderDateFilter);
      fetchDeliveryPersonOrders(selectedDeliveryPerson, orderDateFilter, status);
  };

  // Fonction utilitaire pour obtenir la couleur du statut
  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'confirmed': return 'processing';
      case 'preparing': return 'orange';
      case 'ready_to_deliver': return 'cyan';
      case 'in_delivery': return 'purple';
      case 'delivered': return 'green';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Fonction utilitaire pour obtenir le libellé du statut
  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'confirmed': return 'Confirmé';
      case 'preparing': return 'En préparation';
      case 'ready_to_deliver': return 'Prêt à livrer';
      case 'in_delivery': return 'En livraison';
      case 'delivered': return 'Livré';
      case 'completed': return 'Complété';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  // Colonnes pour le tableau des commandes (adaptées selon si un livreur est sélectionné)
  const getOrderColumns = () => {
    const baseColumns = [
      {
        title: 'N° Commande',
        dataIndex: 'order_number',
        key: 'order_number',
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: 'Client',
        dataIndex: 'customer_name',
        key: 'customer_name',
        ellipsis: true,
      },
      {
        title: 'Adresse',
        dataIndex: 'delivery_address',
        key: 'delivery_address',
        ellipsis: true,
      },
    ];

    // Ajouter la colonne Livreur si aucun livreur spécifique n'est sélectionné
    if (!selectedDeliveryPerson) {
      baseColumns.push({
        title: 'Livreur',
        dataIndex: 'delivery_person_id',
        key: 'delivery_person',
        render: (deliveryPersonId) => {
          const deliveryPerson = deliveryPersons.find(p => p.id === deliveryPersonId);
          return (
            <Text type={deliveryPerson ? 'default' : 'secondary'}>
              {deliveryPerson?.name || 'Non assigné'}
            </Text>
          );
        },
      });
    }

    // Ajouter les colonnes communes
    baseColumns.push(
      {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <Tag color={getOrderStatusColor(status)}>
            {getOrderStatusLabel(status)}
          </Tag>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'total_amount',
        key: 'total_amount',
        render: (amount) => (
          <Text strong>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0)}
          </Text>
        ),
      },
      {
        title: 'Date livraison',
        dataIndex: 'delivery_date',
        key: 'delivery_date',
        render: (date) => date ? new Date(date).toLocaleDateString() : 'Non spécifiée',
        sorter: (a, b) => {
          const dateA = a.delivery_date ? new Date(a.delivery_date) : null;
          const dateB = b.delivery_date ? new Date(b.delivery_date) : null;
          if (dateA && !dateB) return -1;
          if (!dateA && dateB) return 1;
          if (!dateA && !dateB) return 0;
          return dateB - dateA;
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Tooltip title="Voir détails">
            <Button 
              type="link" 
              icon={<FileTextOutlined />} 
              onClick={() => window.open(`/app/orders/${record.id}`, '_blank')}
            />
          </Tooltip>
        ),
      }
    );

    return baseColumns;
  };



  // Fonction pour gérer la sélection des commandes
  const handleOrderSelection = (selectedRowKeys) => {
    setSelectedOrderIds(selectedRowKeys);
  };

  // Fonction pour ouvrir la modale WhatsApp
  const showWhatsappModal = () => {
    if (selectedOrderIds.length === 0) {
      message.warning('Veuillez sélectionner au moins une commande');
      return;
    }
    setWhatsappModalVisible(true);
  };

  // Fonction pour envoyer via WhatsApp
  const handleSendWhatsapp = async (recipient) => {
    try {
      setSendingWhatsapp(true);
      
      // Récupérer les commandes sélectionnées
      const selectedOrders = deliveryPersonOrders.filter(order => 
        selectedOrderIds.includes(order.id)
      );
      
      // Préparer les données pour l'API
      const apiData = {
        orders: selectedOrders,
        recipientType: recipient.type,
        recipientId: recipient.id || recipient.name.toLowerCase().replace(' ', '')
      };
      
      // Envoyer via l'API WhatsApp
      const response = await whatsappApi.sendOrders(apiData);
      
      if (response && response.data && response.data.success) {
        message.success(`Message envoyé avec succès à ${recipient.name}`);
        setWhatsappModalVisible(false);
        setSelectedOrderIds([]); // Reset la sélection
      } else {
        throw new Error(response?.data?.error || 'Erreur inconnue');
      }
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi WhatsApp:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'envoi du message';
      message.error(errorMessage);
    } finally {
      setSendingWhatsapp(false);
    }
  };

  const filteredDeliveryPersons = deliveryPersons.filter(person => 
    person.name.toLowerCase().includes(search.toLowerCase()) ||
    person.phone.includes(search) ||
    (person.email && person.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={2}>Gestion des Livreurs</Title>
          <Link to="/delivery-persons/new">
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
            >
              Nouveau Livreur
            </Button>
          </Link>
        </Space>

        <Tabs defaultActiveKey="list" size="large">
          {/* Onglet Liste des livreurs */}
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                Liste des Livreurs
              </span>
            } 
            key="list"
          >
            <Card>
              <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Input
                  placeholder="Rechercher"
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 300 }}
                />
                <Space>
                  <Radio.Group
                    value={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value={true}>Actifs</Radio.Button>
                    <Radio.Button value={false}>Tous</Radio.Button>
                  </Radio.Group>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchDeliveryPersons}
                  />
                </Space>
              </Space>

              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <DeliveryPersonList 
                  deliveryPersons={filteredDeliveryPersons}
                  onDelete={handleDeleteClick}
                />
              )}
            </Card>
          </TabPane>

          {/* Onglet Commandes par livreur */}
          <TabPane 
            tab={
              <span>
                <TruckOutlined />
                Commandes par Livreur
              </span>
            } 
            key="orders"
          >
            <Card>
              {/* Sélecteur de livreur et filtres */}
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                  <Text strong>Sélectionner un livreur:</Text>
                  <Select
                    style={{ width: '100%', marginTop: 8 }}
                    placeholder="Choisir un livreur"
                    value={selectedDeliveryPerson}
                    onChange={handleDeliveryPersonChange}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {deliveryPersons.filter(p => p.is_active).map(person => (
                      <Option key={person.id} value={person.id}>
                        <Space>
                          <UserOutlined />
                          {person.name}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Text strong>Date:</Text>
                  <DatePicker
                    style={{ width: '100%', marginTop: 8 }}
                    value={orderDateFilter}
                    onChange={handleDateFilterChange}
                    format="DD/MM/YYYY"
                    placeholder="Sélectionner une date"
                  />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Text strong>Statut:</Text>
                  <Select
                    style={{ width: '100%', marginTop: 8 }}
                    value={orderStatusFilter}
                    onChange={handleStatusFilterChange}
                  >
                    <Option value="all">Tous les statuts</Option>
                    <Option value="new">Nouveau</Option>
                    <Option value="confirmed">Confirmé</Option>
                    <Option value="preparing">En préparation</Option>
                    <Option value="ready_to_deliver">Prêt à livrer</Option>
                    <Option value="in_delivery">En livraison</Option>
                    <Option value="delivered">Livré</Option>
                    <Option value="completed">Complété</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={4}>
                  <Text strong>Statistiques:</Text>
                  <div style={{ marginTop: 8 }}>
                    {selectedDeliveryPerson && (
                      <Badge 
                        count={deliveryPersonStats.orders_count || 0} 
                        style={{ backgroundColor: '#52c41a' }}
                      >
                        <Button icon={<CheckCircleOutlined />}>
                          Aujourd'hui
                        </Button>
                      </Badge>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Affichage des commandes */}
              {!selectedDeliveryPerson && !orderDateFilter && orderStatusFilter === 'all' ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>
                  <CarOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Text>Sélectionnez un livreur, une date ou un statut pour voir les commandes</Text>
                </div>
              ) : (
                <div>
                  {/* Affichage du résumé des filtres actifs et actions */}
                  <div style={{ marginBottom: 16, padding: '8px 16px', backgroundColor: '#f0f0f0', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text strong>Filtres actifs : </Text>
                      {selectedDeliveryPerson && (
                        <Tag color="blue">
                          Livreur: {deliveryPersons.find(p => p.id === selectedDeliveryPerson)?.name}
                        </Tag>
                      )}
                      {orderDateFilter && (
                        <Tag color="orange">
                          Date de livraison: {orderDateFilter.format('DD/MM/YYYY')}
                        </Tag>
                      )}
                      {orderStatusFilter && orderStatusFilter !== 'all' && (
                        <Tag color="green">
                          Statut: {getOrderStatusLabel(orderStatusFilter)}
                        </Tag>
                      )}
                      {!selectedDeliveryPerson && (orderDateFilter || (orderStatusFilter && orderStatusFilter !== 'all')) && (
                        <Tag color="purple">Tous les livreurs</Tag>
                      )}
                    </div>
                    
                    <div>
                      {selectedOrderIds.length > 0 && (
                        <Space>
                          <Text type="secondary">{selectedOrderIds.length} commande(s) sélectionnée(s)</Text>
                          <Button 
                            type="primary" 
                            icon={<WhatsAppOutlined />}
                            onClick={showWhatsappModal}
                            style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                          >
                            Envoyer par WhatsApp
                          </Button>
                        </Space>
                      )}
                    </div>
                  </div>
                  
                  {loadingOrders ? (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <Table
                      columns={getOrderColumns()}
                      dataSource={deliveryPersonOrders.map(order => ({ ...order, key: order.id }))}
                      rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys: selectedOrderIds,
                        onChange: handleOrderSelection,
                        getCheckboxProps: (record) => ({
                          name: record.order_number,
                        }),
                      }}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'Aucune commande trouvée pour les critères sélectionnés' }}
                      summary={() => {
                        const columnsCount = getOrderColumns().length;
                        const totalColSpan = selectedDeliveryPerson ? 4 : 5; // Ajuster selon si colonne livreur présente
                        const amountColIndex = selectedDeliveryPerson ? 4 : 5; // Position de la colonne montant
                        const remainingCols = columnsCount - amountColIndex - 1;
                        
                        return (
                          <Table.Summary.Row>
                            <Table.Summary.Cell colSpan={totalColSpan}>
                              <Text strong>
                                Total: {deliveryPersonOrders.length} commande(s)
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell>
                              <Text strong>
                                {new Intl.NumberFormat('fr-FR', { 
                                  style: 'currency', 
                                  currency: 'XOF' 
                                }).format(
                                  deliveryPersonOrders.reduce((sum, order) => sum + (order.total_amount || 0), 0)
                                )}
                              </Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell colSpan={remainingCols}></Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  )}
                </div>
              )}
            </Card>
          </TabPane>
        </Tabs>
      </div>

      {/* Modal de confirmation de suppression */}
      <Modal
        title="Confirmation de suppression"
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        onOk={handleDeleteConfirm}
        okText="Supprimer"
        cancelText="Annuler"
        okButtonProps={{ danger: true }}
      >
        {personToDelete && (
          <p>
            Êtes-vous sûr de vouloir supprimer le livreur <strong>{personToDelete.name}</strong> ? Cette action est irréversible.
          </p>
        )}
      </Modal>

      {/* Modal d'envoi WhatsApp */}
      <Modal
        title={`Envoyer ${selectedOrderIds.length} commande(s) par WhatsApp`}
        open={whatsappModalVisible}
        onCancel={() => setWhatsappModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Choisir le destinataire :</Text>
        </div>
        
        {/* Section Administrateurs */}
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>👥 Administrateurs</Title>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Button
                block
                size="large"
                icon={<WhatsAppOutlined />}
                onClick={() => handleSendWhatsapp({
                  type: 'admin',
                  name: 'Admin 1',
                  id: 'admin1'
                })}
                loading={sendingWhatsapp}
                style={{ height: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              >
                <Text strong>Admin 1</Text>
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                size="large"
                icon={<WhatsAppOutlined />}
                onClick={() => handleSendWhatsapp({
                  type: 'admin',
                  name: 'Admin 2',
                  id: 'admin2'
                })}
                loading={sendingWhatsapp}
                style={{ height: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              >
                <Text strong>Admin 2</Text>
              </Button>
            </Col>
          </Row>
        </div>

        {/* Section Livreurs */}
        <div>
          <Title level={5}>🚚 Livreurs concernés</Title>
          <Row gutter={[8, 8]}>
            {(() => {
              // Récupérer les livreurs uniques des commandes sélectionnées
              const selectedOrders = deliveryPersonOrders.filter(order => 
                selectedOrderIds.includes(order.id)
              );
              
              const uniqueDeliveryPersonIds = [...new Set(
                selectedOrders
                  .filter(order => order.delivery_person_id)
                  .map(order => order.delivery_person_id)
              )];
              
              const concernedDeliveryPersons = deliveryPersons.filter(person => 
                uniqueDeliveryPersonIds.includes(person.id)
              );

              return concernedDeliveryPersons.length > 0 ? (
                concernedDeliveryPersons.map(person => (
                  <Col span={12} key={person.id}>
                                         <Button
                       block
                       size="large"
                       icon={<UserOutlined />}
                       onClick={() => handleSendWhatsapp({
                         type: 'delivery_person',
                         name: person.name,
                         id: person.id
                       })}
                       loading={sendingWhatsapp}
                       style={{ height: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                     >
                       <Text strong>{person.name}</Text>
                     </Button>
                  </Col>
                ))
              ) : (
                <Col span={24}>
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    <Text>Aucun livreur assigné aux commandes sélectionnées</Text>
                  </div>
                </Col>
              );
            })()}
          </Row>
        </div>

        {/* Information */}
        <div style={{ marginTop: 24, padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
          <Text strong>📋 Résumé :</Text>
          <div style={{ marginTop: 8 }}>
            <Text>
              {selectedOrderIds.length} commande(s) seront envoyées au format :
            </Text>
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              📍 Adresse de livraison<br/>
              📱 Numéro de téléphone client<br/>
              💰 Montant total
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DeliveryPersons; 